import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';

import { useTypedSelector } from '@Features/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, Icon, MenuItem } from '@mui/material';
import {
	ClassroomTypes,
	ClassroomLanguages,
	ClassroomSubTypes,
	PaymentMethods,
	ClassroomStatuses,
} from '@Pages/ClassRooms/Constants/Classroom.constants';
import { Divider, GridLayout, Spacer } from '@Styled/utilities';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { DevTool } from '@hookform/devtools';
import { Flatten } from '@Utils/types/Flatten';
import { omit } from 'lodash';
import EdUploadFile from '@Components/UI/Inputs/EdUploadFile/EdUploadFile';
import { useFormUpdater } from '@Hooks/Forms/useFormUpdater';
import { useSnackbar } from '@Providers/useSnackbar';
import { ClassroomRequester } from '@Pages/ClassRooms/Services/Classroom/Requests/Classroom.req';
import {
	ClassroomForm,
	FULL_SCHEMA,
} from '@Pages/ClassRooms/Schema/ClassroomForm.schema';
import { useHistory } from 'react-router-dom';
import { EducationalInfoControlType } from '@Pages/ClassRooms/Types/Classroom.types';
import { ChipFlexLayout } from '@Pages/ClassRooms/Components/AddEditClassroom.styled';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import { getClassroomByLabel } from '@Features/classrooms/classroomSlice';
import { useDispatch } from 'react-redux';

type Props = {
	onHasAdmissionChange: React.Dispatch<boolean>;
};
const EditClassroomBasicInfo: React.FC<Props> = ({ onHasAdmissionChange }) => {
	const history = useHistory();
	const { dropdownInstructors, dropdownCategories } = useTypedSelector(
		(state) => state.dropdowns
	);
	const { displaySnackbar } = useSnackbar();
	const dispatch = useDispatch();

	const { currentClassroom } = useTypedSelector((state) => state.classroom);
	const { dropdownCourses } = useTypedSelector((state) => state.dropdowns);
	const { educational_info, educational_type } = useTypedSelector(
		(state) => state.educational
	);

	const defaultValues = React.useMemo<ClassroomForm>(() => {
		if (currentClassroom) {
			const defaultT: ClassroomForm = {
				...currentClassroom,
				educationLanguages: currentClassroom.educationLanguages
					.filter((_) => _.id !== undefined)
					.map((_) => _.id) as number[],
				educationSections: currentClassroom.educationSections
					.filter((_) => _.id !== undefined)
					.map((_) => _.id) as number[],
				educationTypes: currentClassroom.educationTypes
					.filter((_) => _.id !== undefined)
					.map((_) => _.id) as number[],
				educationYears: currentClassroom.educationYears
					.filter((_) => _.id !== undefined)
					.map((_) => _.id) as number[],
			};

			return defaultT;
		} else {
			return {} as ClassroomForm;
		}
	}, []);

	const {
		formState,
		watch,
		control,
		handleSubmit,
		setValue,
		getValues,
		resetField,
		reset,
	} = useForm<ClassroomForm>({
		resolver: yupResolver(FULL_SCHEMA),
		mode: 'all',
		defaultValues,
	});

	const watchTypes = watch('educationTypes');
	const watchYears = watch('educationYears');
	const watchLanguages = watch('educationLanguages');
	const watchSections = watch('educationSections');

	// Cascading (clearing/pruning child selections) is only correct as a
	// reaction to the USER changing a parent field — never to data arriving.
	// Without this gate, any render where the dropdown tree lags the loaded
	// record wipes DB-saved values and blocks the form on required errors.
	const typesDirty = Boolean(formState.dirtyFields?.educationTypes);
	const yearsDirty = Boolean(formState.dirtyFields?.educationYears);

	const [updater] = useFormUpdater({
		data: defaultValues,
		setValue: setValue,
		keys: [
			'educationSections',
			'educationLanguages',
			'educationTypes',
			'educationYears',
		],
	});

	useEffect(() => {
		updater();
	}, [updater]);

	const years = React.useMemo(() => {
		if (!watchTypes || watchTypes.length === 0) {
			if (typesDirty) setValue('educationYears', []);
			return [];
		}
		let flatYears = educational_info
			.filter((_) => _.id && watchTypes.includes(_.id))
			.map(({ educationYears }) => educationYears)
			.flat();
		flatYears = flatYears.filterDups('id');

		return flatYears;
	}, [watchTypes?.length, educational_info]);

	const languages = React.useMemo(() => {
		if (!watchTypes || watchTypes.length === 0) {
			if (typesDirty) setValue('educationLanguages', []);
			return [];
		}
		let flatLanguages = educational_info
			.filter((_) => _.id && watchTypes.includes(_.id))
			.map(({ educationLanguages }) => educationLanguages)
			.flat();
		flatLanguages = flatLanguages.filterDups('id');

		return flatLanguages;
	}, [watchTypes?.length, educational_info]);

	const sections = React.useMemo(() => {
		if (!watchYears) return;
		if (watchYears.length === 0) {
			if (typesDirty || yearsDirty) setValue('educationSections', []);
			return [];
		}
		let flatYears = educational_info
			.map(({ educationYears }) => educationYears)
			.flat()
			.filterDups('id')
			.filter((_) => watchYears.includes(_.id ?? -1));

		let flatSections = flatYears
			.map(({ educationSections }) => educationSections)
			.flat()
			.filterDups('id');

		return flatSections;
	}, [watchYears?.length, educational_info]);

	const isChildrenDisabled = React.useMemo(() => {
		return !watchTypes || (watchTypes && watchTypes.length === 0);
	}, [watchTypes]);

	const onDeleteValue = (
		control: EducationalInfoControlType,
		value: number
	) => {
		const values = getValues()[control];
		if (!values) return;
		// A new array, not an in-place splice: react-hook-form only notices
		// the change (and re-renders the chips) when the reference changes.
		setValue(
			control,
			values.filter((_) => _ !== value),
			{ shouldValidate: true, shouldDirty: true }
		);
	};

	// Deselecting a parent must drop the child selections that hung off it;
	// otherwise the ids linger as label-less chips and reach the payload.
	// Gated on the parent being USER-dirty: pruning against a dropdown tree
	// that is merely loading (or lagging the record) must never discard the
	// saved values — that wiped Years/Languages on edit and blocked saving.
	useEffect(() => {
		if (!educational_info.length || !typesDirty) return;
		const validYears = new Set(years.map((_) => _.id));
		const validLanguages = new Set(languages.map((_) => _.id));
		const currentYears = getValues('educationYears') ?? [];
		const currentLanguages = getValues('educationLanguages') ?? [];
		if (currentYears.some((id) => !validYears.has(id))) {
			setValue(
				'educationYears',
				currentYears.filter((id) => validYears.has(id)),
				{ shouldValidate: true, shouldDirty: true }
			);
		}
		if (currentLanguages.some((id) => !validLanguages.has(id))) {
			setValue(
				'educationLanguages',
				currentLanguages.filter((id) => validLanguages.has(id)),
				{ shouldValidate: true, shouldDirty: true }
			);
		}
	}, [years, languages, educational_info, typesDirty]);

	useEffect(() => {
		if (!educational_info.length || !sections || (!typesDirty && !yearsDirty)) return;
		const validSections = new Set(sections.map((_) => _.id));
		const currentSections = getValues('educationSections') ?? [];
		if (currentSections.some((id) => !validSections.has(id))) {
			setValue(
				'educationSections',
				currentSections.filter((id) => validSections.has(id)),
				{ shouldValidate: true, shouldDirty: true }
			);
		}
	}, [sections, educational_info, typesDirty, yearsDirty]);

	const hasAdmissionWatch = watch('has_admission');
	useEffect(() => {
		if (hasAdmissionWatch !== undefined) {
			onHasAdmissionChange(hasAdmissionWatch);
		}
	}, [hasAdmissionWatch, onHasAdmissionChange]);

	const onSave = async (form: ClassroomForm) => {
		try {
			const flatten: Flatten<ClassroomForm> = {
				...form,
				educationYears: form.educationYears,
				educationLanguages: form.educationLanguages,
				educationSections: form.educationSections,
				educationTypes: form.educationTypes,
			};
			const normalized = omit(flatten, 'educationalInfo');
			const {
				data: { data },
			} = await ClassroomRequester.getInstance().update(normalized);
			dispatch(
				getClassroomByLabel({ label: currentClassroom?.label as string })
			);
			displaySnackbar(
				'success',
				`Classroom ${data.title} Updated Successfully`
			);
		} catch (e) {
			const msg = e.response?.data.message;
			displaySnackbar('error', msg);
		}
	};
	return (
		<>
			<SectionLayout
				gridGap={'2rem'}
				gridTemplateColumns={{ sm: 'repeat(2,1fr)' }}
			>
				<EdFormControl
					control={control}
					required
					name="title"
					label={'Title'}
				/>

				<EdFormControl
					control={control}
					required
					select
					name="instructor_id"
					label={'Instructor'}
				>
					{dropdownInstructors.map((_, index) => {
						return (
							<MenuItem
								value={_.id}
							>{`${_.first_name} ${_.last_name}`}</MenuItem>
						);
					})}
				</EdFormControl>
				<EdFormControl
					control={control}
					required
					select
					name="category_id"
					label={'Subject'}
				>
					{dropdownCategories.map((_, index) => {
						return <MenuItem value={_.id}>{`${_.name}`}</MenuItem>;
					})}
				</EdFormControl>

				<EdFormControl
					control={control}
					required
					multiline
					name="description"
					label={'Description'}
				/>
				<EdFormControl
					control={control}
					multiline
					name="prerequisites"
					label={'Prerequisites'}
				/>
			</SectionLayout>
			<Divider />
			<SectionLayout>
				<EdFormControl
					control={control}
					required
					name="label"
					label={'Label'}
				/>

				<EdFormControl
					control={control}
					required
					select
					name="type"
					label={'Type'}
				>
					{ClassroomTypes.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_}`} value={_}>
								{_}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<EdFormControl
					control={control}
					required
					select
					name="sub_type"
					label={'Sub Type'}
				>
					{ClassroomSubTypes.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_}`} value={_}>
								{_}
							</MenuItem>
						);
					})}
				</EdFormControl>

				<EdFormControl
					control={control}
					required
					select
					name="language"
					label={'Language'}
				>
					{ClassroomLanguages.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_}`} value={_}>
								{_}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<EdFormControl control={control} required name="code" label={'Code'} />
			</SectionLayout>
			<Divider />
			<SectionLayout>
				<EdFormControl
					control={control}
					required
					select
					name="payment_methods_allowed"
					label={'Payment Methods'}
				>
					{PaymentMethods.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_.value}`} value={_.value}>
								{_.title}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<EdFormControl
					control={control}
					required
					select
					name="status"
					label={'Status'}
				>
					{ClassroomStatuses.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_}`} value={_}>
								{_}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<SectionLayout gridTemplateColumns={'repeat(1,1fr)'}>
					{/* <div> */}
					<EdFormControl
						control={control}
						switch
						name="active"
						label={'Active'}
					/>
					<EdFormControl
						control={control}
						switch
						name="accessible"
						label={'Accessible'}
					/>
					<EdFormControl
						control={control}
						switch
						name="has_admission"
						label={'Has Admission'}
					/>
					<EdFormControl
						control={control}
						switch
						name="auto_accept_admission"
						label="Auto approve requests"
					/>

					<EdFormControl
						control={control}
						switch
						name="admission_status"
						label={'Admission Status'}
					/>
					
					{/* </div> */}
				</SectionLayout>
				<EdFormControl
					control={control}
					name="current_course"
					select
					label="Current course"
				>
					{dropdownCourses.map((_, index) => {
						return (
							<MenuItem key={`${index}-${_.id}`} value={_.id}>
								{_.name}
							</MenuItem>
						);
					})}
				</EdFormControl>
			</SectionLayout>
			<Divider />
			<SectionLayout>
				<EdUploadFile
					required
					folder="classrooms"
					uploadLabel="UPLOAD"
					uploadIcon={<Icon>add</Icon>}
					control={control}
					name="thumbnail"
					label="Cover"
					hint="Recommended 1320 x 780 px. The crop below is locked to 22:13, which is the ratio every surface renders at — card, classroom page and dashboard rows all show the full crop uncropped."
					optimize={false}
				/>
				<EdFormControl
					control={control}
					width="4.75rem"
					type="number"
					inputMode="numeric"
					name="weight"
					label={'Weight'}
				/>
			</SectionLayout>
			<Divider label="Educational Info." />
			<SectionLayout>
				<EdFormControl
					control={control}
					required
					select
					name="educationTypes"
					multiple
					renderValues={(value) => {
						return (
							<div>
								{watchTypes &&
									watchTypes.map((id) => {
										const name = educational_type.find(
											(_) => _.value === id
										)?.name;
										return (
											<SelectChip
												onDelete={() => onDeleteValue('educationTypes', id)}
												label={name}
											/>
										);
									})}
							</div>
						);
					}}
					SelectProps={{
						multiple: true,
						renderValue: (selected) => {
							return (
								<ChipFlexLayout>
									{watchTypes &&
										watchTypes.map((id) => {
											const name = educational_type.find(
												(_) => _.value === id
											)?.name;
											return <>{name},</>;
										})}
								</ChipFlexLayout>
							);
						},
					}}
					label={'Type'}
				>
					{educational_type.map((_, index) => {
						return (
							<MenuItem value={_.value}>
								<Checkbox checked={watchTypes?.includes(_.value)} />
								{_.name}
							</MenuItem>
						);
					})}
				</EdFormControl>

				<EdFormControl
					control={control}
					required
					disabled={isChildrenDisabled}
					select
					multiple
					renderValues={(value) => {
						return (
							<div>
								{watchYears &&
									watchYears.map((id) => {
										const name = years.find((_) => _.id === id)?.name;
										return (
											<SelectChip
												onDelete={() => onDeleteValue('educationYears', id)}
												label={name}
											/>
										);
									})}
							</div>
						);
					}}
					SelectProps={{
						multiple: true,
						renderValue: (selected) => {
							return (
								<ChipFlexLayout>
									{watchYears &&
										watchYears.map((i, index) => {
											const name = years.find((_) => _.id === i)?.name;
											return <>{name},</>;
										})}
								</ChipFlexLayout>
							);
						},
					}}
					name="educationYears"
					label={'Year'}
				>
					{years.map((_, index) => {
						return (
							<MenuItem key={`ed-year-${_.id}`} value={_.id}>
								<Checkbox
									checked={
										_.id !== undefined &&
										watchYears &&
										watchYears.includes(_.id)
									}
								/>

								{_.name}
							</MenuItem>
						);
					})}
				</EdFormControl>
				<EdFormControl
					control={control}
					required
					disabled={isChildrenDisabled}
					select
					multiple
					renderValues={(selected) => {
						return (
							<div>
								{watchLanguages &&
									watchLanguages.map((i, index) => {
										const name = languages.find((_) => _.id === i)?.name;
										return (
											<>
												<SelectChip
													onDelete={() =>
														onDeleteValue('educationLanguages', i)
													}
													label={name}
												/>
											</>
										);
									})}
							</div>
						);
					}}
					SelectProps={{
						multiple: true,
						renderValue: (selected) => {
							return (
								<ChipFlexLayout>
									{watchLanguages?.map((i, index) => {
										const name = languages.find((_) => _.id === i)?.name;
										return <>{name},</>;
									})}
								</ChipFlexLayout>
							);
						},
					}}
					name="educationLanguages"
					label={'Language'}
				>
					{languages.map((_, index) => {
						return (
							<MenuItem key={`ed-lng-${_.id}`} value={_.id}>
								<Checkbox
									checked={
										_.id !== undefined &&
										watchLanguages &&
										watchLanguages.includes(_.id)
									}
								/>
								{_.name}
							</MenuItem>
						);
					})}
				</EdFormControl>

				{sections && sections.length > 0 && (
					<EdFormControl
						control={control}
						required
						disabled={isChildrenDisabled}
						select
						multiple
						renderValues={(selected) => {
							return (
								<div>
									{watchSections?.map((i, index) => {
										const name = sections.find((_) => _.id === i)?.name;
										return (
											<SelectChip
												onDelete={() =>
													onDeleteValue('educationSections', i as number)
												}
												label={name}
											/>
										);
									})}
								</div>
							);
						}}
						SelectProps={{
							multiple: true,
							renderValue: (selected) => {
								return (
									<ChipFlexLayout>
										{watchSections &&
											watchSections.map((i, index) => {
												const name = sections.find((_) => _.id === i)?.name;
												return <>{name},</>;
											})}
									</ChipFlexLayout>
								);
							},
						}}
						name="educationSections"
						label={'Section'}
					>
						{sections.map((_, index) => {
							return (
								<MenuItem key={`ed-lng-${_.id}`} value={_.id}>
									<Checkbox
										checked={
											_.id !== undefined &&
											watchSections &&
											watchSections.includes(_.id)
										}
									/>
									{_.name}
								</MenuItem>
							);
						})}
					</EdFormControl>
				)}
			</SectionLayout>
			<ActiosnContainer>
				<Button
					disabled={!formState.isDirty}
					variant="contained"
					color="warning"
					onClick={() => history.push('/dashboard/classrooms')}
				>
					Cancel
				</Button>
				<Spacer mx={'.5rem'} />
				<Button
					onClick={() => {
						onSave(getValues());
					}}
					disabled={!formState.isDirty || !formState.isValid}
					variant="contained"
				>
					Update
				</Button>
			</ActiosnContainer>
			 
		</>
	);
};
const UploadWrapper = styled.div`
	display: grid;
	grid-template-columns: 70% 30%;
	grid-gap: 1rem;
`;
const SectionLayout = styled(GridLayout)`
	/* margin-right: 5.5rem; */
	margin-right: 2.5rem;
	grid-gap: 2rem;
`;
const ActiosnContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 8.313rem;
`;

export default EditClassroomBasicInfo;
